import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeElementsOptions,
} from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import { LoaderCircle, X } from 'lucide-react';
import { useSetupIntentPopup } from '../../hooks/useSetupIntentPopup';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import {
    assertSuccessResponse,
    assertErrorResponse,
} from '~/modules/root/graphql/responses';

// Type definition for confirmSetup method
interface StripeWithConfirmSetup extends Stripe {
    confirmSetup(options: {
        elements: StripeElements;
        confirmParams: {
            return_url: string;
        };
        redirect: 'if_required';
    }): Promise<{ error?: { message?: string } }>;
}

// Type definition for Elements options
interface ElementsOptions extends StripeElementsOptions {
    clientSecret: string;
    appearance: {
        theme: 'stripe';
        variables: {
            colorPrimary: string;
            colorBackground: string;
            colorText: string;
            colorDanger: string;
            fontFamily: string;
            spacingUnit: string;
            borderRadius: string;
        };
    };
}

/**
 * Setup form component that handles Stripe payment element for setup intents
 */
const SetupForm: React.FC<{
    onSetupSuccess?: () => void;
    onSetupError?: (error: string) => void;
}> = ({ onSetupSuccess, onSetupError }) => {
    const { closePopup } = useSetupIntentPopup();
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements || !isPaymentElementReady) {
            return;
        }

        setIsProcessing(true);
        setMessage(null);

        try {
            // Confirm the setup using the payment element
            const { error } = await (
                stripe as StripeWithConfirmSetup
            ).confirmSetup({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (error) {
                setMessage(error.message || 'An error occurred during setup');
                onSetupError?.(error.message || 'Setup failed');
            } else {
                setMessage('Payment method saved successfully!');
                onSetupSuccess?.();
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : 'An unexpected error occurred';
            setMessage(errorMessage);
            onSetupError?.(errorMessage);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6" id="setup-form">
            <PaymentElement
                onReady={() => setIsPaymentElementReady(true)}
                options={{
                    layout: 'tabs',
                }}
            />
            {message && (
                <div
                    className={`rounded-md p-3 text-sm ${
                        message.includes('successful')
                            ? 'bg-green-50 text-green-700 border-green-200 border'
                            : 'border border-red-200 bg-red-50 text-red-700'
                    }`}
                >
                    {message}
                </div>
            )}
            <div className="flex flex-row gap-3 pb-8 md:pb-0">
                <Button
                    type="submit"
                    disabled={
                        !stripe ||
                        !elements ||
                        !isPaymentElementReady ||
                        isProcessing
                    }
                    className="flex-1"
                >
                    {isProcessing ? (
                        <>
                            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        'Pay Now'
                    )}
                </Button>
                <Button
                    type="button"
                    color="white"
                    onClick={closePopup}
                    disabled={isProcessing}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
};

/**
 * Stripe setup intent popup component with payment element
 */
export const StripeSetupIntentPopup: React.FC<{
    onSetupSuccess?: () => void;
    onSetupError?: (error: string) => void;
}> = ({ onSetupSuccess, onSetupError }) => {
    const {
        isSetupIntentPopupOpen,
        closePopup,
        currentCustomerId,
        stripeAccount,
        paymentData,
        invoicePaymentId,
    } = useSetupIntentPopup();

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const hasCreatedSetupIntent = useRef(false);
    const config = useConfigContext();

    // Create Stripe promise using publishable key from configuration
    const stripePromise = useMemo(() => {
        const publishableKey = config.stripeEcommerce.publishableKey;

        // Create Stripe instance with stripeAccount if available
        // We need to include stripeAccount for Stripe Connect functionality
        return loadStripe(publishableKey, {
            stripeAccount: stripeAccount || undefined,
        });
    }, [stripeAccount, config.stripeEcommerce.publishableKey]);

    useEffect(() => {
        if (
            isSetupIntentPopupOpen &&
            currentCustomerId &&
            !hasCreatedSetupIntent.current
        ) {
            // Create setup intent via GraphQL mutation when popup opens
            setIsLoading(true);
            hasCreatedSetupIntent.current = true;

            const createSetupIntentViaAPI = async () => {
                try {
                    const formData = new FormData();
                    formData.append('contactId', String(currentCustomerId));
                    if (invoicePaymentId) {
                        formData.append('invoicePaymentId', invoicePaymentId);
                    }

                    const response = await fetch(
                        '/remix-api/setup-intent/create',
                        {
                            method: 'POST',
                            body: formData,
                        }
                    );

                    const result = await response.json();

                    if (assertSuccessResponse(result) && result.data) {
                        const data = result.data as {
                            clientSecret: string;
                            id: string;
                        };
                        setClientSecret(data.clientSecret);
                        setIsLoading(false);
                    } else {
                        const errorMessage = assertErrorResponse(result)
                            ? result.message
                            : 'Failed to create setup intent';
                        onSetupError?.(errorMessage);
                        setIsLoading(false);
                    }
                } catch (err) {
                    const errorMessage =
                        err instanceof Error
                            ? err.message
                            : 'Failed to create setup intent';
                    onSetupError?.(errorMessage);
                    setIsLoading(false);
                }
            };

            createSetupIntentViaAPI();
        } else if (!isSetupIntentPopupOpen) {
            // Reset when popup closes
            setClientSecret(null);
            setIsLoading(false);
            hasCreatedSetupIntent.current = false;
        }
    }, [
        isSetupIntentPopupOpen,
        currentCustomerId,
        onSetupError,
        closePopup,
        invoicePaymentId,
    ]);

    // Handle browser back button to close popup on mobile
    useEffect(() => {
        if (isSetupIntentPopupOpen) {
            // Push a dummy state to the history when dialog opens
            window.history.pushState({ setupIntentPopup: true }, '');

            // Listen for back button press
            const handlePopState = (_event: PopStateEvent) => {
                // Close the popup when back button is pressed
                closePopup();
            };

            window.addEventListener('popstate', handlePopState);

            // Cleanup function - only remove the event listener
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        } else if (window.history.state?.setupIntentPopup) {
            // Remove the dummy state only when the popup is closed
            window.history.back();
        }
    }, [isSetupIntentPopupOpen, closePopup]);

    const options: ElementsOptions | undefined = clientSecret
        ? {
              clientSecret,
              appearance: {
                  theme: 'stripe' as const,
                  variables: {
                      colorPrimary: '#0570de',
                      colorBackground: '#ffffff',
                      colorText: '#30313d',
                      colorDanger: '#df1b41',
                      fontFamily: 'Ideal Sans, system-ui, sans-serif',
                      spacingUnit: '2px',
                      borderRadius: '4px',
                  },
              },
          }
        : undefined;

    return (
        <Dialog open={isSetupIntentPopupOpen}>
            <DialogContent
                closeButton={false}
                className="max-h-[100vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span>Complete Payment</span>
                        <Button
                            color="white"
                            size="compact"
                            onClick={closePopup}
                            className="h-6 w-6 p-0"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </DialogTitle>
                    <DialogDescription>
                        Enter your payment details to securely complete your
                        purchase.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {paymentData && (
                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                Amount to pay
                            </p>
                            <p className="text-2xl font-semibold">
                                {(paymentData.currency?.toUpperCase() ||
                                    'USD') +
                                    ' ' +
                                    paymentData.amount.toFixed(2)}
                            </p>
                            {/* Note: Stripe amounts are in cents, so we divide by 100 to display the correct amount */}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <LoaderCircle className="h-6 w-6 animate-spin" />
                            <span className="ml-2 text-sm text-gray-600">
                                Processing payment method...
                            </span>
                        </div>
                    ) : (
                        !(options && stripeAccount) && (
                            <div className="flex items-center justify-center py-8">
                                <LoaderCircle className="h-6 w-6 animate-spin" />
                                <span className="ml-2 text-sm text-gray-600">
                                    Initializing payment method...
                                </span>
                            </div>
                        )
                    )}
                    {options && stripeAccount && (
                        <Elements stripe={stripePromise} options={options}>
                            <SetupForm
                                onSetupSuccess={() => {
                                    closePopup();
                                    onSetupSuccess?.();
                                }}
                                onSetupError={(error) => {
                                    onSetupError?.(error);
                                }}
                            />
                        </Elements>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
